import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useRoleDetection } from '@/hooks/useRoleDetection';
import Layout from '@/components/Layout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredFeature?: 'fohTasks' | 'setups' | 'kitchen' | 'documentation' | 'training' | 'evaluations' | 'leadership';
  requiresFullAccess?: boolean;
  minLevel?: number; // Minimum position level required (1-10)
}

/**
 * A resolved reason the current user may not see this route. Computed during
 * render but acted on from an effect — telling the user why is a side effect,
 * and firing it inline updates the notification context while this component
 * is still rendering, which React warns about.
 */
interface AccessDenial {
  title: string;
  message: string;
  redirectTo: string;
  /** When set, the notification is shown at most once per session for this key. */
  onceKey?: string;
}

/**
 * A wrapper component that protects routes based on authentication and subscription status
 * @param children The component to render if the user is authenticated and has access
 * @param requiredFeature Optional feature that is required to access this route
 * @param minLevel Optional minimum position level required (1=Team Member, 2=Trainer, 3=Leader, 4=Director)
 */
export function ProtectedRoute({ children, requiredFeature, requiresFullAccess, minLevel }: ProtectedRouteProps) {
  const { user, isLoading, hasNetworkIssue } = useAuth();
  const { isFeatureEnabled } = useSubscription();
  const { showNotification } = useNotification();
  const { positionLevel } = useRoleDetection();
  const location = useLocation();

  // Resolve why (if at all) this user is blocked. Order matters and matches the
  // original checks: position level, then full access, then subscription feature.
  const denial = React.useMemo<AccessDenial | null>(() => {
    if (isLoading || !user) return null;

    // Check if user has minimum position level when required
    if (minLevel !== undefined && minLevel !== null && positionLevel < minLevel) {
      const levelNames = ['', 'Team Member', 'Trainer', 'Leader', 'Director'];
      const requiredLevelName = levelNames[minLevel] || `Level ${minLevel}`;
      return {
        title: 'Access Restricted',
        message: `This feature requires ${requiredLevelName} level or higher. Please contact your administrator if you need access.`,
        redirectTo: '/dashboard'
      };
    }

    // Check if user has full access when required
    if (requiresFullAccess && (user.accessLevel === 'leadership-only' || user.accessLevel === 'limited')) {
      return {
        title: 'Access Restricted',
        message: 'You do not have permission to access this feature. Please contact your administrator if you need access.',
        redirectTo: '/leadership'
      };
    }

    // If a specific feature is required, check if it's enabled
    if (requiredFeature && !isFeatureEnabled(requiredFeature)) {
      // Format the feature name to be more readable
      const featureNames = {
        'fohTasks': 'FOH Tasks',
        'setups': 'Setup Sheet',
        'kitchen': 'Kitchen',
        'documentation': 'Documentation',
        'training': 'Training',
        'evaluations': 'Evaluations',
        'leadership': 'Leadership'
      };

      const featureName = featureNames[requiredFeature] || requiredFeature;

      return {
        title: 'Feature Not Available',
        message: `The ${featureName} feature is not enabled in your subscription. You are being redirected to subscription settings.`,
        // Redirect to settings page with subscription tab active and feature highlighted
        redirectTo: `/settings?tab=subscription&requiredFeature=${requiredFeature}`,
        onceKey: `feature_toast_${requiredFeature}`
      };
    }

    return null;
  }, [isLoading, user, minLevel, positionLevel, requiresFullAccess, requiredFeature, isFeatureEnabled]);

  // Tell the user why they were bounced — after commit, never during render.
  React.useEffect(() => {
    if (!denial) return;
    if (denial.onceKey) {
      if (sessionStorage.getItem(denial.onceKey)) return;
      sessionStorage.setItem(denial.onceKey, 'true');
    }
    showNotification('warning', denial.title, denial.message);
  }, [denial, showNotification]);

  // Show loading state while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Avoid redirecting to login during network issues or while validation retries are running
  if (!user) {
    if (hasNetworkIssue || isLoading) {
      return <Layout><div className="p-4 text-center">Reconnecting… You are still signed in.</div></Layout>;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (denial) {
    return <Navigate to={denial.redirectTo} replace />;
  }

  // User is authenticated and has access to the required feature
  return <Layout>{children}</Layout>;
}


import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();

  // Wait for auth state
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Require authentication
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only allow the main site admin
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin routes handle their own layout
  return <>{children}</>;
}
